<template>
  <div class="slides-online-container">
    <b-modal
      size="lg"
      ref="loading-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-container>
        <b-row>
          <b-col class="text-center" v-if="!link">
            <pretty-loading />
          </b-col>
          <b-col class="text-center" v-else>
            <b-button pill variant="primary" @click="downloadPptxFile">
              Descargar!
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
    <!-- Color Palette Selector -->
    <div class="color-palette-selector">
      <!--<b-button @click="remake">Remake</b-button>-->
      <p class="palette-title">Elige los colores de tu presentación</p>
      <div class="palette-grid">
        <div
          v-for="(palette, key) in colorPalettes"
          :key="key"
          class="palette-option"
          :class="{ active: selectedPalette === key }"
          :data-palette="key"
          :data-name="palette.name"
          :style="{
            '--option-primary': palette.primary,
            '--option-light': palette.primaryLight,
          }"
          @click="selectPalette(key)"
          @keydown="handlePaletteKeydown($event, key)"
          tabindex="0"
        ></div>
      </div>
    </div>
    <div class="container">
      <!-- Header Principal (Slide 0) -->
      <div
        class="hero"
        v-if="slidesData && slidesData.slides && slidesData.slides.length > 0"
      >
        <h1
          v-html="md(slidesData.slides[0] && slidesData.slides[0].title)"
        ></h1>
        <p
          v-html="md(slidesData.slides[0] && slidesData.slides[0].subtitle)"
        ></p>
      </div>
      <!-- Estado vacio: es lo primero que ve un profesor que llega por primera
           vez, asi que dice lo que esta pasando en vez de lo que falta.
           Si la sesion se cayo no se muestra: prometer diapositivas que ya no
           van a llegar contradice el aviso de mas abajo. -->
      <div v-else-if="!sesionCaida" class="no-slides-message">
        <p class="no-slides-title">Estamos armando tu presentación ✨</p>
        <p class="no-slides-hint">
          Las diapositivas van a ir apareciendo acá, una por una.
        </p>
      </div>

      <!-- Slides dinámicas (a partir del slide 1) -->
      <div v-for="(slide, index) in slidesData.slides.slice(1)" :key="index">
        <single-slide-component
          :index="index + 1"
          :dailyPlanification="dailyPlanification"
          :demo="demo"
          :demoSlide="demo && demoSlides ? demoSlides[index + 1] : null"
          :slideExterna="demo ? null : slide"
          @slideUpdate="slideUpdate"
          @slideDone="slideDone"
          @revelada="$emit('revelada', $event)"
        />
      </div>

      <!-- Una sola burbuja a la vez. Antes esta condicion miraba solo el largo
           de `slides`, que arranca vacio: la burbuja de las slides aparecia
           junto con la de la tabla y el profesor veia dos girando al mismo
           tiempo. `loading` (que ya se recibia por prop, sin usarse) es
           verdadero recien cuando la tabla termino y empieza el stream de las
           slides. -->
      <!-- Misma burbuja de acuarela que la tabla, para que las dos fases del
           proceso se sientan del mismo producto. Necesita escenario propio
           porque el loader es un overlay absoluto. -->
      <!-- `loading` se apaga cuando la vitrina termino de mostrar la ultima
           lamina. Antes esto miraba tambien el largo del arreglo, asi que los
           botones de descarga aparecian al empujar la slide 12, mientras su
           texto y su imagen todavia se estaban revelando. -->
      <!-- Si la sesion se cayo, el mazo no puede continuar: se dice con todas
           sus letras y se ofrece la salida, en vez de dejar la burbuja girando
           indefinidamente sobre algo que ya no va a llegar. -->
      <div v-if="sesionCaida" class="sesion-caida">
        <p class="sesion-caida-titulo">Se cerró tu sesión ⏱️</p>
        <p class="sesion-caida-texto">
          Tu planificación está lista y la puedes descargar acá abajo, pero para
          terminar las diapositivas necesitas volver a entrar.
        </p>
        <button class="sesion-caida-boton" @click="$emit('reingresar')">
          Volver a entrar
        </button>
      </div>
      <div v-else-if="loading" class="slides-loader-stage">
        <MaterialStreamLoader
          :messages="loaderMessages"
          morph-message="La idea se convierte en acuarela…"
          open-message="Preparando tus diapositivas…"
          :hold-ms="0"
        />
      </div>

      <!-- Botón de descarga de PDF -->
      <div
        class="download-pdf-wrapper"
        v-else
        style="
          text-align: center;
          margin-top: 32px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 16px;
          justify-content: center;
        "
      >
        <!-- La descarga del Word vive arriba (en PlanificationTable) y se pierde
             al bajar hasta las slides: para cuando el profesor llega al final,
             el boton quedo varias pantallas atras. Se duplica aca, al lado del
             de PPT, reutilizando su downloadDocument() via evento.
             Antes esto era `v-if="demo"` y solo aparecia en el demo publico. -->
        <button
          class="download-word-btn"
          @click="$emit('download-word')"
        >
          <div class="btn-content">
            <span class="icon">⬇️</span>
            <span>Descarga Word Planificación</span>
          </div>
        </button>

        <!-- Eliminar botón PDF -->

        <button
          class="download-btn"
          :class="{
            processing: isDownloading,
            'ready-to-download': isReadyToDownload,
            completed: isCompleted,
          }"
          @click="handleClick"
        >
          <div class="progress-bar" :style="{ width: progress + '%' }"></div>
          <div class="btn-content">
            <span v-if="!isDownloading && !isCompleted" class="icon">⚡</span>
            <div v-if="isDownloading" class="spinner"></div>
            <div v-if="isCompleted" class="success-orb"></div>
            <span>{{ buttonText }}</span>
          </div>
        </button>

        <!--<button
          class="download-pdf-btn"
          style="background: #8c5383"
          :disabled="
            !slidesData ||
            !slidesData.slides ||
            slidesData.slides.length !== 12 ||
            downloadingPptx
          "
          @click="downloadSlidesPptx"
        >
          <span v-if="!downloadingPptx">⬇️ Descargar PPT</span>
          <span v-else-if="downloadingPptx">Generando PPT...</span>
          <span
            v-else-if="
              slidesData && slidesData.slides && slidesData.slides.length < 12
            "
          >
            Generando diapositvas {{ slidesData.slides.length }} de 11
          </span>
        </button>-->
      </div>
    </div>
  </div>
</template>

<script>
import assistedMaterialService from "@/services/assistedMaterialService";
import SingleSlideComponent from "./SingleSlideComponent";
import PrettyLoading from "./PrettyLoading.vue";
import MaterialStreamLoader from "./MaterialStreamLoader.vue";
import { followElementTop, watchUserScroll } from "@/utils/scrollToTop";
import { inlineMarkdown } from "@/utils/inlineMarkdown";

export default {
  name: "SlidesOnlineComponent",
  components: {
    SingleSlideComponent,
    PrettyLoading,
    MaterialStreamLoader
  },
  props: {
    slidesData: {
      type: Object,
      default: () => ({ slides: [] }),
    },
    // La sesion en el servidor principal se cayo: no se pueden pedir mas
    // diapositivas. Se avisa en vez de dejar la burbuja girando para siempre.
    sesionCaida: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dailyPlanification: {
      type: Object,
      default: () => ({}),
    },
    // Demo mode (public planification demo): slides come pre-recorded via
    // `demoSlides` and the PPTX download is a static file (`demoPptUrl`), so
    // neither the worker nor the websocket is touched.
    demo: {
      type: Boolean,
      default: false,
    },
    demoSlides: {
      type: Array,
      default: null,
    },
    demoPptUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedPalette: "cyan",
      colorPalettes: {
        cyan: {
          name: "Cian Vibrante",
          primary: "#20B2B2",
          primaryLight: "#4ECDC4",
          primaryDark: "#1A9999",
          secondary: "#DDF093",
          accent: "#8C5383",
          textColor: "white",
        },
        coral: {
          name: "Rojo Coral",
          primary: "#FF5A5F",
          primaryLight: "#FF8A8E",
          primaryDark: "#E64A50",
          secondary: "#20B2B2",
          accent: "#8C5383",
          textColor: "white",
        },
        violet: {
          name: "Violeta Grisáceo",
          primary: "#8C5383",
          primaryLight: "#B07AA6",
          primaryDark: "#6E4267",
          secondary: "#DDF093",
          accent: "#FF5A5F",
          textColor: "white",
        },
        burgundy: {
          name: "Burdeos",
          primary: "#64113F",
          primaryLight: "#943A5E",
          primaryDark: "#4A0C2E",
          secondary: "#DDF093",
          accent: "#20B2B2",
          textColor: "white",
        },
      },
      loaderMessages: [
        [
          "Diseñando tus <em>diapositivas</em>",
          "Eligiendo las imágenes y el orden de cada lámina…",
        ],
        [
          "Mezclando educación con <em>amor</em>",
          "Cuidando que cada slide se entienda de una mirada…",
        ],
      ],
      // Seguimiento del scroll mientras van entrando las slides.
      scrollEstado: { usuarioSeFue: false, pendiente: false },
      dejarDeVigilarScroll: null,
      downloadingPptx: false,
      link: "",
      currentDone: 0,
      isDownloading: false,
      isCompleted: false,
      isReadyToDownload: false,
      progress: 0,
      demoProgressTimer: null
    };
  },
  beforeDestroy() {
    if (this.demoProgressTimer) clearInterval(this.demoProgressTimer);
    if (this.dejarDeVigilarScroll) this.dejarDeVigilarScroll();
  },
  computed: {
    buttonText() {
      // El texto habla del RESULTADO que quiere el profesor (su PPT), no del
      // paso tecnico que ocurre por dentro. "Crear Archivo!" obligaba a
      // adivinar que iba a pasar despues de apretarlo.
      if (this.isCompleted) return '¡Listo! Descarga tu PPT';
      if (this.isDownloading) {
        const total = this.slidesData && this.slidesData.slides
          ? this.slidesData.slides.length
          : 0;
        return total
          ? `Preparando el archivo para tu descarga… (${this.currentDone}/${total})`
          : 'Preparando el archivo para tu descarga…';
      }
      if (this.isReadyToDownload) return 'Descargar PPT';
      return this.demo ? 'Descargar PPT de la clase' : 'Descargar PPT';
    }
  },
  mounted() {
    this.dejarDeVigilarScroll = watchUserScroll(this.scrollEstado);
    this.applyColorPalette(this.selectedPalette);
    // Anonymous demo visitors have no authenticated socket; the PPTX comes
    // from a static file instead, so skip the listener entirely.
    if (this.demo) return;
    this.$options.sockets.onmessage = (data) => {
      const message = JSON.parse(data.data).message;
      if (message) {
        if (message.kind === "pptx_ready") {
          this.link = message.link;
          this.progress = 100;
          this.isDownloading = false;
          this.isReadyToDownload = true;
        }
        else if (message.kind === "slide_done") {
          this.currentDone = message.index;
          this.progress = (message.index+1)*100/13
        }
      }
    };
  },
  methods: {
    // El hero lo escribe el modelo igual que las slides, asi que pasa por el
    // mismo conversor: escapa y traduce `**negrita**`, `<b>` y `++subrayado++`.
    md(value) {
      return inlineMarkdown(value);
    },
    handleClick() {
      if (this.isDownloading) return;
      if (this.demo && this.demoPptUrl) {
        // Demo: static pre-generated file — one click downloads it directly
        // (no separate "create file" step, and it stays within the user
        // gesture so the browser doesn't block the download).
        this.link = this.demoPptUrl;
        this.downloadPptxFile();
        return;
      }
      if (!this.link) this.startDownload();
      if (this.link) this.downloadPptxFile();
    },
    startDownload() {
      this.isDownloading = true;
      this.progress = 5;
      if (this.demo && this.demoPptUrl) {
        // Simulate the generation progress, then point the download button at
        // the pre-generated static PPTX.
        this.demoProgressTimer = setInterval(() => {
          this.progress = Math.min(this.progress + Math.random() * 8 + 6, 100);
          if (this.progress >= 100) {
            clearInterval(this.demoProgressTimer);
            this.demoProgressTimer = null;
            this.link = this.demoPptUrl;
            this.isDownloading = false;
            this.isReadyToDownload = true;
          }
        }, 150);
        return;
      }
      this.downloadSlidesPptx();

      /*
      let downloadProgress = 0;
      const interval = setInterval(() => {
        downloadProgress += Math.random() * 8 + 6;
        if (downloadProgress >= 100) {
          downloadProgress = 100;
          clearInterval(interval);
          this.completeDownload();
        }
        this.progress = downloadProgress;
      }, 150);
      */
    },
    completeDownload() {
      this.isDownloading = false;
      this.isCompleted = true;
    },
    resetButton() {
      this.isCompleted = false;
      this.isReadyToDownload = false;
      // Note: 'isCreating' is controlled by the parent component's 'slides' prop.
      // The parent component should reset the prop to '0' to start the process again.
      // We'll set it to false here, but the watcher will immediately set it to true if slides > 0.
    },
    slideUpdate(payload) {
      this.$emit("slideUpdate", payload);
    },
    slideDone(payload) {
      this.$emit("slideDone", payload);
    },
    remake() {
      this.$emit("remake");
    },
    downloadPptxFile() {
      const a = document.createElement("a");
      a.href = this.link;
      a.setAttribute("download", ""); // Forzar descarga
      a.setAttribute("target", "_blank"); // por si no descarga directamente
      document.body.appendChild(a);

      // Simular clic
      a.click();

      // Eliminar link temporal
      document.body.removeChild(a);

      this.link = "";
      this.downloadingPptx = false;
    },
    selectPalette(paletteName) {
      this.selectedPalette = paletteName;
      this.applyColorPalette(paletteName);
    },
    handlePaletteKeydown(event, paletteName) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.selectPalette(paletteName);
      }
    },
    applyColorPalette(paletteName) {
      const palette = this.colorPalettes[paletteName];
      const root = document.documentElement;

      root.style.setProperty("--primary", palette.primary);
      root.style.setProperty("--primary-light", palette.primaryLight);
      root.style.setProperty("--primary-dark", palette.primaryDark);
      root.style.setProperty("--secondary", palette.secondary);
      root.style.setProperty("--accent", palette.accent);
      root.style.setProperty(
        "--gradient-primary",
        `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)`
      );

      const primaryRgb = this.hexToRgb(palette.primary);

      if (primaryRgb) {
        root.style.setProperty(
          "--primary-bg-light",
          `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.06)`
        );
        root.style.setProperty(
          "--primary-bg-medium",
          `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.04)`
        );
        root.style.setProperty(
          "--primary-bg-strong",
          `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.08)`
        );
        root.style.setProperty(
          "--primary-border-light",
          `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.12)`
        );
        // `--primary-border-medium` la usan varios bloques de las slides
        // (.assertion-section y .final-case de la slide 10, entre otros) pero
        // no se definia en ninguna parte ni se actualizaba aca. Un
        // `border: 2px solid var(--variable-inexistente)` es invalido, asi que
        // el borde caia a `currentColor`: se veia de un color fijo que no
        // cambiaba nunca al elegir otra paleta.
        root.style.setProperty(
          "--primary-border-medium",
          `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.22)`
        );
        root.style.setProperty(
          "--primary-border-medium",
          `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.15)`
        );
      }
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    },
    async downloadSlidesPptx() {
      if (
        !this.slidesData ||
        !this.slidesData.slides ||
        this.slidesData.slides.length === 0
      )
        return;
      try {
        assistedMaterialService
          .downloadSlidesPPTX({
            slides: this.slidesData.slides,
            palette: this.colorPalettes[this.selectedPalette],
          })
          .then((data) => {
            console.log(data);
            // this.downloadingPptx = true;
          });
      } catch (error) {
        console.error("[FRONTEND] Error descargando PPTX:", error);
        alert("Error al descargar el PPTX: " + error.message);
      }
    },
  },
  watch: {
    // El seguimiento del scroll se cortaba al terminar la tabla: de ahi para
    // abajo habia que bajar a mano hasta las slides. Ahora la seccion de slides
    // tambien acompana su propio crecimiento.
    slidesData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          // Seguir la ULTIMA SLIDE, no el contenedor completo: debajo de las
          // slides vive la burbuja de carga, asi que seguir el fondo del
          // contenedor dejaba al profesor mirando el cargando en vez de la
          // diapositiva que se acaba de dibujar.
          const slides = this.$el.querySelectorAll(".slide");
          const objetivo = slides.length ? slides[slides.length - 1] : null;
          if (objetivo) followElementTop(objetivo, this.scrollEstado);
        });
      },
    },
    downloadingPptx() {
      if (this.downloadingPptx) {
        this.$refs["loading-modal"].show();
      } else {
        this.$refs["loading-modal"].hide();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sesion-caida {
  text-align: center;
  padding: 32px 24px;
  margin: 12px 0;
  border-radius: 16px;
  background: var(--primary-bg-light);
  border: 1px solid var(--primary-border-medium);
}

.sesion-caida-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.sesion-caida-texto {
  margin: 0 auto 18px;
  max-width: 460px;
  color: var(--text-primary);
  opacity: 0.8;
}

.sesion-caida-boton {
  border: 0;
  border-radius: 999px;
  padding: 10px 26px;
  font-weight: 600;
  color: #fff;
  background: var(--primary);
  cursor: pointer;
}

/* Escenario del loader de slides: el overlay de acuarela se posiciona sobre
   este contenedor, asi que la altura vive aca. */
.slides-loader-stage {
  position: relative;
  min-height: 320px;
  margin: 12px 0;
  border-radius: 28px;
  overflow: hidden;
}

@media (max-width: 576px) {
  .slides-loader-stage {
    min-height: 260px;
  }
}

/* Estado vacio de las slides. Antes era un `<p>` suelto sin estilo, con el texto
   en negativo ("No hay slides generadas aun"). */
.no-slides-message {
  text-align: center;
  padding: 48px 24px;
}

.no-slides-message .no-slides-title {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--text-primary);
}

.no-slides-message .no-slides-hint {
  margin: 0;
  opacity: 0.7;
  color: var(--text-primary);
}

.slides-online-container {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  background: var(--bg-secondary);
  color: var(--text-primary);
  line-height: 1.6;
  min-height: 100vh;
  transition: all 0.3s ease;
}

/* Color Palette Selector */
/* Selector de paleta.
   Antes era una grilla de `auto-fit` con `max-width: 800px` y `aspect-ratio: 1`:
   con cuatro paletas daban cuadrados de ~190x190, que dominaban la pantalla y
   competian con la presentacion. Ahora son pastillas de 44px en una fila
   centrada, con el nombre debajo en vez de encima del color. */
.slides-online-container .color-palette-selector {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: 14px 20px;
  margin-bottom: 20px;
  border: 1px solid var(--border);
  animation: fadeIn 0.6s ease-out;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px 18px;
}

.slides-online-container .palette-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
  opacity: 0.75;
}

.slides-online-container .palette-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
}

.slides-online-container .palette-option {
  /* Columna propia por opcion: el nombre es mas ancho que el circulo, asi que
     si el elemento midiera solo 44px los nombres de paletas vecinas se pisaban
     ("Violeta Grisaceo" encima de "Burdeos"). El ancho lo manda el texto. */
  width: 100px;
  height: 62px;
  position: relative;
  cursor: pointer;
  background: none;
  border: 0;
  padding: 0;
}

.slides-online-container .palette-option:focus-visible {
  outline: none;
}

/* El circulo de color, centrado arriba. El anillo del activo va aca y no en el
   elemento completo: antes envolvia circulo + nombre y salia un ovalo. */
.slides-online-container .palette-option::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: linear-gradient(
    135deg,
    var(--option-primary) 0%,
    var(--option-light) 100%
  );
  box-shadow: var(--shadow-sm);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.slides-online-container .palette-option:hover::before {
  transform: translateX(-50%) translateY(-2px);
  box-shadow: var(--shadow-md);
}

.slides-online-container .palette-option.active::before,
.slides-online-container .palette-option:focus-visible::before {
  box-shadow: 0 0 0 2px var(--bg-primary), 0 0 0 4px var(--text-primary);
}

/* Nombre debajo, ocupando todo el ancho de la columna y centrado. */
.slides-online-container .palette-option::after {
  content: attr(data-name);
  position: absolute;
  top: 46px;
  left: 0;
  right: 0;
  text-align: center;
  color: var(--text-primary);
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.65;
}

.slides-online-container .palette-option.active::after {
  opacity: 1;
}

.slides-online-container .container {
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 0;
  padding-right: 0;
  display: grid;
  gap: 20px;
  animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header Principal */
.slides-online-container .hero {
  background: var(--gradient-primary);
  color: white;
  padding: 32px 28px;
  border-radius: var(--radius-lg);
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
}

.slides-online-container .hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at 70% 30%,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 50%
  );
  pointer-events: none;
}

.slides-online-container .hero h1 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}

.slides-online-container .hero p {
  font-size: 1.1rem;
  opacity: 0.9;
  position: relative;
  z-index: 1;
  max-width: 600px;
  margin: 0 auto;
}

/* Cards de Slides */

.download-pdf-btn {
  background: var(--primary, #20b2b2);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  margin: 0 auto;
  cursor: pointer;
  box-shadow: var(--shadow-md);
  transition: background 0.2s, box-shadow 0.2s;
}
.download-pdf-btn:disabled {
  background: #ccc;
  color: #888;
  cursor: not-allowed;
  box-shadow: none;
}

.download-btn {
  position: relative;
  width: 100%;
  height: 70px;
  border: none;
  border-radius: 35px;
  background: linear-gradient(135deg, #4ECDC4, #44A08D);
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  margin-bottom: 25px;
  box-shadow: 0 10px 30px rgba(78, 205, 196, 0.3);
}

/* Same shape/size as the PPT button (.download-btn), Word-blue palette so the
   two downloads read as a cohesive pair instead of clashing. */
.download-word-btn {
  position: relative;
  width: 100%;
  height: 70px;
  border: none;
  border-radius: 35px;
  background: linear-gradient(135deg, #4a90d9, #2b579a);
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 10px 30px rgba(43, 87, 154, 0.3);
}
.download-word-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 15px 40px rgba(43, 87, 154, 0.4);
}

.download-btn:hover:not(.processing):not(.completed) {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 15px 40px rgba(78, 205, 196, 0.4);
}

.download-btn.processing {
  cursor: not-allowed;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.download-btn.ready-to-download {
  background: linear-gradient(135deg, #FFB74D, #FF8A65);
  box-shadow: 
    0 0 25px rgba(255, 183, 77, 0.6),
    0 10px 30px rgba(255, 138, 101, 0.4);
  animation: readyPulse 2s ease-in-out infinite;
}

@keyframes readyPulse {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 
      0 0 25px rgba(255, 183, 77, 0.6),
      0 10px 30px rgba(255, 138, 101, 0.4);
  }
  50% { 
    transform: scale(1.02);
    box-shadow: 
      0 0 35px rgba(255, 183, 77, 0.8),
      0 15px 40px rgba(255, 138, 101, 0.6);
  }
}

.download-btn.completed {
  background: linear-gradient(45deg, #4ECDC4, #C47AC0, #FFB74D, #4ECDC4);
  background-size: 300% 300%;
  animation: gradientShift 3s ease infinite;
  box-shadow: 
    0 0 30px rgba(78, 205, 196, 0.5),
    0 0 60px rgba(196, 122, 192, 0.3),
    0 0 90px rgba(255, 183, 77, 0.2);
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.btn-content {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  transition: all 0.3s ease;
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(135deg, #C47AC0, #FFB74D);
  width: 0%;
  transition: width 0.3s ease;
  border-radius: 35px;
  box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.2);
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), transparent);
  border-radius: 35px 35px 0 0;
}

.progress-text {
  font-size: 15px;
  color: #555;
  margin-top: 15px;
  font-weight: 500;
}

.icon {
  margin-right: 10px;
  font-size: 20px;
  transition: transform 0.3s ease;
}

.download-btn:hover .icon {
  transform: scale(1.1);
}
</style>

<!-- BLOQUE GLOBAL PARA FORZAR TARJETA DE SLIDE -->
<style>
.slides-online-container .slide {
  background: var(--bg-primary) !important;
  border-radius: var(--radius-lg) !important;
  overflow: hidden !important;
  box-shadow: var(--shadow-sm) !important;
  border: 1px solid var(--border) !important;
  transition: all 0.3s ease !important;
}
</style>

<!-- VARIABLES CSS GLOBALES -->
<style>
:root {
  --primary: #20b2aa;
  --primary-light: #4ecdc4;
  --primary-dark: #189b93;
  --secondary: #f8ac04;
  --accent: #c6588d;
  --text-primary: #1a202c;
  --text-secondary: #4a5568;
  --text-muted: #718096;
  --bg-primary: #ffffff;
  --bg-secondary: #f8fafc;
  --bg-tertiary: #edf2f7;
  --border: #e2e8f0;
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.08);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --gradient-primary: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-light) 100%
  );
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
}
</style>
